import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  Building2, 
  Plus,
  Search,
  Settings,
  Users,
  ShoppingCart,
  Target,
  MoreVertical,
  Edit,
  Trash2,
  Power,
  ChevronLeft,
  Globe,
  Mail,
  Phone,
  MapPin,
  Check,
  X,
  AlertCircle,
  MessageSquare,
  CreditCard,
  BookOpen,
  Link2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Integration icons and colors
const INTEGRATION_CONFIG = {
  shopify: { icon: ShoppingCart, color: 'green', name: 'Shopify' },
  whatsapp: { icon: MessageSquare, color: 'emerald', name: 'WhatsApp' },
  mamopay: { icon: CreditCard, color: 'purple', name: 'MamoPay' },
  zoho: { icon: BookOpen, color: 'blue', name: 'Zoho' },
  meta: { icon: MessageSquare, color: 'emerald', name: 'Meta' }
};

// Available modules for companies
const AVAILABLE_MODULES = [
  { key: 'dashboard', label: 'Dashboard', description: 'Main dashboard and analytics' },
  { key: 'orders', label: 'Orders', description: 'Order management and tracking' },
  { key: 'leads', label: 'Leads', description: 'Lead management and sales pipeline' },
  { key: 'customers', label: 'Customers', description: 'Customer database and CRM' },
  { key: 'products', label: 'Products', description: 'Product catalog management' },
  { key: 'inventory', label: 'Inventory', description: 'Stock and warehouse management' },
  { key: 'vendors', label: 'Vendors', description: 'Vendor management' },
  { key: 'purchase_orders', label: 'Purchase Orders', description: 'PO creation and tracking' },
  { key: 'deliveries', label: 'Deliveries', description: 'Delivery management' },
  { key: 'employees', label: 'Employees', description: 'Employee management' },
  { key: 'attendance', label: 'Attendance', description: 'Attendance tracking' },
  { key: 'payroll', label: 'Payroll', description: 'Payroll processing' },
  { key: 'leaves', label: 'Leaves', description: 'Leave management' },
  { key: 'cash_management', label: 'Cash Management', description: 'Cash flow tracking' },
  { key: 'reports', label: 'Reports', description: 'Reporting and analytics' },
  { key: 'marketing', label: 'Marketing', description: 'Marketing tools' },
  { key: 'journeys', label: 'Journeys', description: 'Customer journey builder' },
  { key: 'inbox', label: 'Inbox', description: 'Unified inbox' },
  { key: 'whatsapp', label: 'WhatsApp', description: 'WhatsApp integration' },
  { key: 'integrations', label: 'Integrations', description: 'Third-party integrations' },
  { key: 'settings', label: 'Settings', description: 'System settings' },
  { key: 'audit_log', label: 'Audit Log', description: 'Activity logging' },
  { key: 'api_keys', label: 'API Keys', description: 'API key management' },
];

const SuperAdminCompaniesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showModulesDialog, setShowModulesDialog] = useState(false);
  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [creating, setCreating] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Integration status per company
  const [companyIntegrations, setCompanyIntegrations] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: 'UAE',
    currency: 'AED',
    timezone: 'Asia/Dubai',
    industry: ''
  });

  useEffect(() => {
    if (user && !user.is_super_admin) {
      toast.error('Access denied. Super Admin only.');
      navigate('/dashboard');
      return;
    }
    fetchCompanies();
  }, [user, navigate]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const companiesList = response.data.companies || [];
      setCompanies(companiesList);
      
      // Fetch integrations for each company
      const integrationsMap = {};
      await Promise.all(
        companiesList.map(async (company) => {
          try {
            const intRes = await axios.get(
              `${API_BASE_URL}/companies/${company.id}/integrations`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            integrationsMap[company.id] = intRes.data.integrations || {};
          } catch {
            integrationsMap[company.id] = {};
          }
        })
      );
      setCompanyIntegrations(integrationsMap);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!formData.name.trim()) {
      toast.error('Company name is required');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/companies`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Company created successfully');
      setShowCreateDialog(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        country: 'UAE',
        currency: 'AED',
        timezone: 'Asia/Dubai',
        industry: ''
      });
      fetchCompanies();
    } catch (error) {
      console.error('Failed to create company:', error);
      toast.error(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateStatus = async (companyId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/companies/${companyId}/status?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Company status updated to ${newStatus}`);
      fetchCompanies();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update company status');
    }
  };

  const loadCompanyUsers = async (companyId) => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/super-admin/companies/${companyId}/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanyUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load company users');
      setCompanyUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUpdateModules = async (modules) => {
    if (!selectedCompany) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/super-admin/companies/${selectedCompany.id}/modules`,
        modules,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state without closing dialog
      setSelectedCompany(prev => ({
        ...prev,
        modules_enabled: response.data.modules_enabled
      }));
      
      toast.success('Module updated');
      // Don't close dialog - let user toggle multiple modules
      // Refresh companies list in background
      fetchCompanies();
    } catch (error) {
      console.error('Failed to update modules:', error);
      toast.error('Failed to update modules');
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         company.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || company.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="super-admin-companies">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/super-admin')}
              className="text-wt-text-secondary hover:text-wt-text-primary -ml-2"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Companies</h1>
          <p className="text-wt-text-secondary">Manage all registered companies</p>
        </div>
        <Button 
          onClick={() => navigate('/super-admin/onboarding')}
          className="bg-wt-gold hover:bg-wt-gold-dark text-black"
          data-testid="new-company-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Company
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-secondary" />
          <Input
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-wt-surface border-wt-border text-wt-text-primary"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] bg-wt-surface border-wt-border text-wt-text-primary">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-wt-surface border-wt-border">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCompanies.map((company) => (
          <Card 
            key={company.id} 
            className="bg-wt-surface border-wt-border hover:border-wt-gold/50 transition-colors"
          >
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-wt-gold/10 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-wt-gold" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-wt-text-primary">{company.name}</CardTitle>
                    <CardDescription className="text-wt-text-secondary text-xs">
                      {company.slug}
                    </CardDescription>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-wt-text-secondary">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-wt-surface border-wt-border">
                    <DropdownMenuItem 
                      onClick={() => navigate(`/super-admin/companies/${company.id}/settings`)}
                      className="text-wt-text-primary"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Details
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => {
                        setSelectedCompany(company);
                        setShowModulesDialog(true);
                      }}
                      className="text-wt-text-primary"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Manage Modules
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => {
                        setSelectedCompany(company);
                        setShowUsersDialog(true);
                        loadCompanyUsers(company.id);
                      }}
                      className="text-wt-text-primary"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      View Users
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate(`/super-admin/companies/${company.id}/billing`)}
                      className="text-wt-text-primary"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Billing & Plans
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-wt-border" />
                    {company.status === 'active' ? (
                      <DropdownMenuItem 
                        onClick={() => handleUpdateStatus(company.id, 'suspended')}
                        className="text-orange-400"
                      >
                        <Power className="h-4 w-4 mr-2" />
                        Suspend
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem 
                        onClick={() => handleUpdateStatus(company.id, 'active')}
                        className="text-green-400"
                      >
                        <Power className="h-4 w-4 mr-2" />
                        Activate
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Contact Info */}
                <div className="space-y-1 text-sm">
                  {company.email && (
                    <div className="flex items-center gap-2 text-wt-text-secondary">
                      <Mail className="h-3 w-3" />
                      <span>{company.email}</span>
                    </div>
                  )}
                  {company.country && (
                    <div className="flex items-center gap-2 text-wt-text-secondary">
                      <MapPin className="h-3 w-3" />
                      <span>{company.country}</span>
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-wt-border">
                  <div className="text-center">
                    <p className="text-xs text-wt-text-secondary">Users</p>
                    <p className="font-semibold text-wt-text-primary">
                      {companies.find(c => c.id === company.id)?.stats?.users || 0}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-wt-text-secondary">Orders</p>
                    <p className="font-semibold text-wt-text-primary">
                      {companies.find(c => c.id === company.id)?.stats?.orders || 0}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-wt-text-secondary">Leads</p>
                    <p className="font-semibold text-wt-text-primary">
                      {companies.find(c => c.id === company.id)?.stats?.leads || 0}
                    </p>
                  </div>
                </div>

                {/* Integration Status Indicators */}
                <TooltipProvider>
                  <div className="flex items-center gap-2 pt-2 border-t border-wt-border">
                    <span className="text-xs text-wt-text-secondary mr-1">
                      <Link2 className="h-3 w-3 inline mr-1" />
                      Integrations:
                    </span>
                    {Object.entries(INTEGRATION_CONFIG).map(([key, config]) => {
                      const integration = companyIntegrations[company.id]?.[key];
                      const status = integration?.status || (integration ? 'unknown' : 'not_configured');
                      const Icon = config.icon;
                      
                      return (
                        <Tooltip key={key}>
                          <TooltipTrigger asChild>
                            <div className={`
                              w-6 h-6 rounded flex items-center justify-center cursor-pointer
                              ${status === 'connected' ? 'bg-green-500/10' : 
                                status === 'error' ? 'bg-red-500/10' : 
                                status === 'unknown' ? 'bg-yellow-500/10' :
                                'bg-gray-500/10'}
                            `}>
                              <Icon className={`h-3 w-3 ${
                                status === 'connected' ? 'text-green-400' :
                                status === 'error' ? 'text-red-400' :
                                status === 'unknown' ? 'text-yellow-400' :
                                'text-gray-500'
                              }`} />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="bg-wt-surface border-wt-border">
                            <div className="text-xs">
                              <p className="font-medium">{config.name}</p>
                              <p className={`${
                                status === 'connected' ? 'text-green-400' :
                                status === 'error' ? 'text-red-400' :
                                status === 'unknown' ? 'text-yellow-400' :
                                'text-gray-400'
                              }`}>
                                {status === 'connected' ? 'Connected' :
                                 status === 'error' ? `Error: ${integration?.last_error || 'Unknown'}` :
                                 status === 'unknown' ? 'Not validated' :
                                 'Not configured'}
                              </p>
                              {integration?.last_validated_at && (
                                <p className="text-wt-text-muted mt-1">
                                  Last checked: {new Date(integration.last_validated_at).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </TooltipProvider>

                {/* Status Badge */}
                <div className="flex items-center justify-between pt-2">
                  <Badge 
                    variant={company.status === 'active' ? 'default' : 'secondary'}
                    className={
                      company.status === 'active' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                      company.status === 'suspended' ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' :
                      'bg-gray-500/10 text-gray-400 border-gray-500/20'
                    }
                  >
                    {company.status}
                  </Badge>
                  <span className="text-xs text-wt-text-secondary">
                    {company.subscription?.plan || 'enterprise'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredCompanies.length === 0 && (
          <div className="col-span-full text-center py-12 text-wt-text-secondary">
            <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No companies found</p>
          </div>
        )}
      </div>

      {/* Create Company Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-wt-surface border-wt-border sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">Create New Company</DialogTitle>
            <DialogDescription className="text-wt-text-secondary">
              Add a new company to the platform
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Company Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter company name"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@company.com"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+971 XX XXX XXXX"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Country</Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    <SelectItem value="UAE">UAE</SelectItem>
                    <SelectItem value="Saudi Arabia">Saudi Arabia</SelectItem>
                    <SelectItem value="India">India</SelectItem>
                    <SelectItem value="USA">USA</SelectItem>
                    <SelectItem value="UK">UK</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-wt-text-primary">Currency</Label>
                <Select 
                  value={formData.currency} 
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger className="bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    <SelectItem value="AED">AED</SelectItem>
                    <SelectItem value="SAR">SAR</SelectItem>
                    <SelectItem value="INR">INR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-wt-text-primary">Industry</Label>
              <Input
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                placeholder="e.g., Furniture, Electronics, Fashion"
                className="bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowCreateDialog(false)}
              className="border-wt-border text-wt-text-primary"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCompany}
              disabled={creating}
              className="bg-wt-gold hover:bg-wt-gold-dark text-black"
            >
              {creating ? 'Creating...' : 'Create Company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Modules Dialog */}
      <Dialog open={showModulesDialog} onOpenChange={setShowModulesDialog}>
        <DialogContent className="bg-wt-surface border-wt-border sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">
              Manage Modules - {selectedCompany?.name}
            </DialogTitle>
            <DialogDescription className="text-wt-text-secondary">
              Enable or disable modules for this company
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {AVAILABLE_MODULES.map((module) => {
              const isEnabled = selectedCompany?.modules_enabled?.[module.key] ?? 
                              (Object.keys(selectedCompany?.modules_enabled || {}).length === 0);
              
              return (
                <div 
                  key={module.key}
                  className="flex items-center justify-between p-3 rounded-lg bg-wt-bg border border-wt-border"
                >
                  <div>
                    <p className="font-medium text-wt-text-primary">{module.label}</p>
                    <p className="text-xs text-wt-text-secondary">{module.description}</p>
                  </div>
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={(checked) => {
                      handleUpdateModules({ [module.key]: checked });
                    }}
                  />
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowModulesDialog(false)}
              className="border-wt-border text-wt-text-primary"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Users Dialog */}
      <Dialog open={showUsersDialog} onOpenChange={setShowUsersDialog}>
        <DialogContent className="bg-wt-surface border-wt-border sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Users className="h-5 w-5 text-wt-gold" />
              Users - {selectedCompany?.name}
            </DialogTitle>
            <DialogDescription className="text-wt-text-secondary">
              {companyUsers.length} user(s) in this company
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {loadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-wt-gold"></div>
              </div>
            ) : companyUsers.length === 0 ? (
              <div className="text-center py-8 text-wt-text-secondary">
                No users found in this company
              </div>
            ) : (
              companyUsers.map((user) => (
                <div 
                  key={user.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-wt-bg border border-wt-border"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-wt-gold/10 flex items-center justify-center">
                      <span className="text-wt-gold font-medium">
                        {(user.full_name || user.email || '?')[0].toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-wt-text-primary">{user.full_name || 'No name'}</p>
                      <p className="text-xs text-wt-text-secondary">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {user.is_admin && (
                      <Badge className="bg-wt-gold/10 text-wt-gold border-wt-gold/20">
                        Admin
                      </Badge>
                    )}
                    <Badge className={user.is_active ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowUsersDialog(false)}
              className="border-wt-border text-wt-text-primary"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminCompaniesPage;
